import React, { useState, useEffect, useRef, useCallback } from 'react';

// --- Constants ---
const CANVAS_WIDTH = 600;
const CANVAS_HEIGHT = 600;
const SNOWFLAKE_MIN_SIZE = 8;  // 최소 눈꽃 크기
const SNOWFLAKE_MAX_SIZE = 25; // 최대 눈꽃 크기
const MAX_SNOWFLAKES = 80;     // 최대 눈꽃 개수
const SPAWN_RATE = 0.8;        // 눈꽃 생성 확률 (프레임당)
const GRAVITY = 0.1;           // 중력 계수
const WIND_CHANGE = 0.1;       // 바람 변화 속도

const SnowflakeFractal = () => {
  // --- State and Refs ---
  const canvasRef = useRef(null);
  const [snowflakes, setSnowflakes] = useState([]);
  const [isRunning, setIsRunning] = useState(true);
  const animationFrameId = useRef(null);
  const windForce = useRef(0);
  const frameCount = useRef(0);

  // --- Snowflake Pattern Generators ---
  const generateKochPattern = (size) => {
    // 코흐 눈꽃 패턴 (6방향 대칭)
    const points = [];
    const angleStep = Math.PI * 2 / 6;
    
    for (let i = 0; i < 6; i++) {
      const angle = i * angleStep;
      const mainLine = { 
        x1: 0, 
        y1: 0, 
        x2: Math.cos(angle) * size, 
        y2: Math.sin(angle) * size 
      };
      
      // 주가지 추가
      points.push(mainLine);
      
      // 작은 가지 추가 (각 방향에 2-3개)
      const branches = Math.floor(Math.random() * 2) + 2;
      for (let j = 0; j < branches; j++) {
        const branchLength = size * (0.3 + Math.random() * 0.3);
        const branchAngle = angle + (Math.random() * 0.5 - 0.25);
        const branchStart = Math.random() * 0.7 + 0.2; // 주가지 어디에서 분기할지
        
        const startX = mainLine.x1 + (mainLine.x2 - mainLine.x1) * branchStart;
        const startY = mainLine.y1 + (mainLine.y2 - mainLine.y1) * branchStart;
        
        points.push({
          x1: startX,
          y1: startY,
          x2: startX + Math.cos(branchAngle) * branchLength,
          y2: startY + Math.sin(branchAngle) * branchLength
        });
      }
    }
    
    return points;
  };
  
  const generateStarPattern = (size) => {
    // 별 모양 눈꽃 패턴
    const points = [];
    const spikes = Math.floor(Math.random() * 3) + 4; // 4-6개의 가시 
    const angleStep = Math.PI * 2 / spikes;
    const innerRadius = size * 0.4;
    
    for (let i = 0; i < spikes; i++) {
      const outerAngle = i * angleStep;
      const innerAngle = outerAngle + angleStep / 2;
      
      // 외부 꼭지점
      const outerX = Math.cos(outerAngle) * size;
      const outerY = Math.sin(outerAngle) * size;
      
      // 내부 꼭지점
      const innerX = Math.cos(innerAngle) * innerRadius;
      const innerY = Math.sin(innerAngle) * innerRadius;
      
      // 이전 내부 꼭지점
      const prevInnerAngle = ((i - 1 + spikes) % spikes) * angleStep + angleStep / 2;
      const prevInnerX = Math.cos(prevInnerAngle) * innerRadius;
      const prevInnerY = Math.sin(prevInnerAngle) * innerRadius;
      
      // 선분 추가
      points.push({ x1: 0, y1: 0, x2: outerX, y2: outerY });
      points.push({ x1: prevInnerX, y1: prevInnerY, x2: outerX, y2: outerY });
      points.push({ x1: outerX, y1: outerY, x2: innerX, y2: innerY });
    }
    
    return points;
  };
  
  const generateCrystalPattern = (size) => {
    // 결정형 눈꽃 패턴 (십자가 + 대각선)
    const points = [];
    const angleStep = Math.PI * 2 / 8;
    
    for (let i = 0; i < 8; i++) {
      const angle = i * angleStep;
      const mainLine = { 
        x1: 0, 
        y1: 0, 
        x2: Math.cos(angle) * size, 
        y2: Math.sin(angle) * size 
      };
      
      points.push(mainLine);
      
      // 주가지의 중간에 작은 가지 추가
      if (i % 2 === 0) { // 4방향(십자가)에만 추가 가지
        const midX = mainLine.x1 + (mainLine.x2 - mainLine.x1) * 0.6;
        const midY = mainLine.y1 + (mainLine.y2 - mainLine.y1) * 0.6;
        
        // 양쪽으로 작은 가지 추가
        const perpAngle1 = angle + Math.PI / 2;
        const perpAngle2 = angle - Math.PI / 2;
        const branchLength = size * 0.3;
        
        points.push({
          x1: midX,
          y1: midY,
          x2: midX + Math.cos(perpAngle1) * branchLength,
          y2: midY + Math.sin(perpAngle1) * branchLength
        });
        
        points.push({
          x1: midX,
          y1: midY,
          x2: midX + Math.cos(perpAngle2) * branchLength,
          y2: midY + Math.sin(perpAngle2) * branchLength
        });
      }
    }
    
    return points;
  };
  
  const getRandomPattern = (size) => {
    const patterns = [generateKochPattern, generateStarPattern, generateCrystalPattern];
    const selectedPattern = patterns[Math.floor(Math.random() * patterns.length)];
    return selectedPattern(size);
  };
  
  // --- Snowflake Creation ---
  const createSnowflake = useCallback(() => {
    const size = SNOWFLAKE_MIN_SIZE + Math.random() * (SNOWFLAKE_MAX_SIZE - SNOWFLAKE_MIN_SIZE);
    const x = Math.random() * CANVAS_WIDTH;
    const y = -size * 2; // 화면 위에서 시작
    
    return {
      x,
      y,
      size,
      pattern: getRandomPattern(size),
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.02,
      speedX: 0,
      speedY: 0.5 + Math.random() * 1,
      opacity: 0.7 + Math.random() * 0.3,
      color: `hsl(${200 + Math.random() * 40}, ${5 + Math.random() * 10}%, ${90 + Math.random() * 10}%)`
    };
  }, []);
  
  // --- Drawing Function ---
  const draw = useCallback((ctx, currentSnowflakes) => {
    // 배경 - 밤하늘 효과
    ctx.fillStyle = '#0a1428';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // 배경 그라데이션 추가
    const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
    gradient.addColorStop(0, 'rgba(20, 40, 80, 0.8)');
    gradient.addColorStop(1, 'rgba(10, 20, 40, 0.9)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // 작은 빛나는 별들 추가
    for (let i = 0; i < 100; i++) {
      const starSize = Math.random() * 1.5;
      const opacity = Math.random() * 0.7 + 0.3;
      ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
      ctx.beginPath();
      ctx.arc(
        Math.random() * CANVAS_WIDTH, 
        Math.random() * CANVAS_HEIGHT, 
        starSize, 0, Math.PI * 2
      );
      ctx.fill();
    }
    
    // 눈꽃송이 그리기
    currentSnowflakes.forEach(flake => {
      ctx.save();
      ctx.translate(flake.x, flake.y);
      ctx.rotate(flake.rotation);
      ctx.strokeStyle = flake.color;
      ctx.lineWidth = 1.5;
      ctx.globalAlpha = flake.opacity;
      
      // 눈꽃 패턴의 각 선분 그리기
      flake.pattern.forEach(line => {
        ctx.beginPath();
        ctx.moveTo(line.x1, line.y1);
        ctx.lineTo(line.x2, line.y2);
        ctx.stroke();
        
        // 빛나는 효과 추가
        ctx.lineWidth = 0.5;
        ctx.shadowColor = 'white';
        ctx.shadowBlur = 4;
        ctx.stroke();
      });
      
      ctx.restore();
    });
  }, []);
  
  // --- Animation Function ---
  const updateSnowflakes = useCallback(() => {
    return snowflakes => {
      // 윈드 효과 업데이트
      windForce.current += (Math.random() - 0.5) * WIND_CHANGE;
      windForce.current *= 0.98; // 감쇠
      const currentWind = windForce.current;
      
      // 새 눈꽃 추가
      let newSnowflakes = [...snowflakes];
      
      // 일정 확률로 새로운 눈꽃 생성
      if (Math.random() < SPAWN_RATE && newSnowflakes.length < MAX_SNOWFLAKES) {
        newSnowflakes.push(createSnowflake());
      }
      
      // 눈꽃 움직임 업데이트
      newSnowflakes = newSnowflakes.map(flake => {
        // 중력과 바람 적용
        flake.speedY += GRAVITY * (flake.size / SNOWFLAKE_MAX_SIZE); // 큰 눈꽃이 더 빨리 떨어짐
        flake.speedX = currentWind * (flake.size / SNOWFLAKE_MIN_SIZE * 0.5); // 작은 눈꽃이 바람에 더 많이 영향받음
        
        // 좌우 흔들림 추가
        flake.speedX += (Math.random() - 0.5) * 0.1;
        
        // 위치 업데이트
        flake.x += flake.speedX;
        flake.y += flake.speedY;
        
        // 회전 업데이트
        flake.rotation += flake.rotationSpeed;
        
        return flake;
      });
      
      // 화면 밖으로 나간 눈꽃 제거
      newSnowflakes = newSnowflakes.filter(flake => 
        flake.y < CANVAS_HEIGHT + flake.size * 2 &&
        flake.x > -flake.size * 2 &&
        flake.x < CANVAS_WIDTH + flake.size * 2
      );
      
      return newSnowflakes;
    };
  }, [createSnowflake]);
  
  // --- Animation Loop ---
  const startAnimation = useCallback(() => {
    if (animationFrameId.current) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    const animate = () => {
      frameCount.current++;
      
      // 눈꽃 업데이트
      setSnowflakes(updateSnowflakes());
      
      // 그리기
      draw(ctx, snowflakes);
      
      // 다음 프레임 요청
      if (isRunning) {
        animationFrameId.current = requestAnimationFrame(animate);
      } else {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
          animationFrameId.current = null;
        }
      }
    };
    
    frameCount.current = 0;
    animationFrameId.current = requestAnimationFrame(animate);
  }, [isRunning, draw, snowflakes, updateSnowflakes]);
  
  // --- Simulation Control Effect ---
  useEffect(() => {
    if (isRunning) {
      startAnimation();
    } else {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
    }
    
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
    };
  }, [isRunning, startAnimation]);
  
  // --- Initialization Effect ---
  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = CANVAS_WIDTH;
    canvas.height = CANVAS_HEIGHT;
    
    // 초기 눈꽃 생성
    const initialSnowflakes = [];
    for (let i = 0; i < 20; i++) {
      const flake = createSnowflake();
      flake.y = Math.random() * CANVAS_HEIGHT; // 화면 전체에 분포
      initialSnowflakes.push(flake);
    }
    
    setSnowflakes(initialSnowflakes);
    
    const ctx = canvas.getContext('2d');
    draw(ctx, initialSnowflakes);
  }, [createSnowflake, draw]);
  
  // --- Button Handlers ---
  const handleToggleRun = () => {
    setIsRunning(prev => !prev);
  };
  
  const handleReset = useCallback(() => {
    setIsRunning(false);
    
    // 모든 눈꽃 재생성
    const initialSnowflakes = [];
    for (let i = 0; i < 20; i++) {
      const flake = createSnowflake();
      flake.y = Math.random() * CANVAS_HEIGHT; // 화면 전체에 분포
      initialSnowflakes.push(flake);
    }
    
    setSnowflakes(initialSnowflakes);
    windForce.current = 0;
    
    // 화면 다시 그리기
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      draw(ctx, initialSnowflakes);
    }
    
    // 잠시 후 다시 시작
    setTimeout(() => setIsRunning(true), 100);
  }, [createSnowflake, draw]);
  
  // --- JSX ---
  return (
    <div style={containerStyle}>
      <h2 style={titleStyle}>Snowflake Crystal Simulation</h2>
      <div style={canvasContainerStyle}>
        <canvas ref={canvasRef} width={CANVAS_WIDTH} height={CANVAS_HEIGHT} style={canvasStyle} />
      </div>
      <div style={controlsStyle}>
        <button onClick={handleToggleRun} style={isRunning ? pauseButtonStyle : playButtonStyle}>
          {isRunning ? '일시정지' : '재생'}
        </button>
        <button onClick={handleReset} style={resetButtonStyle}>
          리셋
        </button>
      </div>
      <div style={infoStyle}>
        <span>떨어지는 눈꽃 결정체: <strong>{snowflakes.length}</strong></span>
      </div>
    </div>
  );
};

// Modern styled components
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2rem',
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #0a1428 0%, #0f2650 100%)',
  color: '#e0e0e0',
  fontFamily: 'Arial, sans-serif'
};

const titleStyle = {
  margin: '0 0 1.5rem 0',
  color: '#ffffff',
  fontSize: '2rem',
  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
  letterSpacing: '1px'
};

const canvasContainerStyle = {
  padding: '0.5rem',
  background: 'rgba(255,255,255,0.05)',
  borderRadius: '12px',
  boxShadow: '0 10px 20px rgba(0,0,0,0.3), 0 6px 6px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1) inset',
  overflow: 'hidden'
};

const canvasStyle = {
  display: 'block',
  borderRadius: '8px',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.05) inset'
};

const controlsStyle = {
  display: 'flex',
  gap: '1rem',
  marginTop: '1.5rem'
};

const buttonBaseStyle = {
  padding: '0.75rem 1.5rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  cursor: 'pointer',
  border: 'none',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.2)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  outline: 'none'
};

const playButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #4f9cf9 0%, #2872b9 100%)',
  color: 'white'
};

const pauseButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #6babff 0%, #4a94f3 100%)',
  color: 'white'
};

const resetButtonStyle = {
  ...buttonBaseStyle,
  background: 'linear-gradient(135deg, #607D8B 0%, #455A64 100%)',
  color: 'white'
};

const infoStyle = {
  marginTop: '1.5rem',
  padding: '0.75rem 1.25rem',
  background: 'rgba(255,255,255,0.08)',
  borderRadius: '8px',
  fontSize: '0.95rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  letterSpacing: '0.5px'
};

export default SnowflakeFractal;
